import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContracts } from '../../hooks/useContracts';
import { ContractList } from './ContractList';
import { ContractFilters } from './ContractFilters';
import type { ContractStatus } from '@shared/types';

export function ReviewDashboard() {
  const navigate = useNavigate();
  const { contracts, loading, reload } = useContracts();
  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out archived contracts by default unless explicitly viewing archived
  const activeContracts = contracts.filter((c) => c.status !== 'archived');
  
  const filteredContracts = contracts.filter((contract) => {
    // If viewing 'all', exclude archived contracts
    const matchesStatus = 
      statusFilter === 'all' 
        ? contract.status !== 'archived'
        : contract.status === statusFilter;
    
    const matchesSearch = 
      contract.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contract.title?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      contract.executiveSummary?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const statusCounts = {
    all: activeContracts.length, // Only count active (non-archived) contracts
    draft: contracts.filter((c) => c.status === 'draft').length,
    review: contracts.filter((c) => c.status === 'review').length,
    signature: contracts.filter((c) => c.status === 'signature').length,
    executed: contracts.filter((c) => c.status === 'executed').length,
    archived: contracts.filter((c) => c.status === 'archived').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contracts</h1>
            <p className="mt-2 text-gray-600">
              Manage and review all your contracts
            </p>
          </div>
          <button
            onClick={() => navigate('/contracts/new')}
            className="px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
          >
            Create New Contract
          </button>
        </div>

        <ContractFilters
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          statusCounts={statusCounts}
        />

        <div className="mt-6">
          <ContractList
            contracts={filteredContracts}
            loading={loading}
            onRefresh={reload}
          />
        </div>
      </div>
    </div>
  );
}

